import { useEffect, useState } from 'react';
import Link from 'next/link';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { markConversationQuestionSeen, orderConversationQuestions } from '../../lib/community/conversationRotation';
import {
  type GroceryConversationPrompt,
  type PromptCategory,
} from '../../lib/community/inspiration';

type Props = {
  onUseIdea: (question: string, category: PromptCategory) => void;
  refreshKey: number;
};

const ROTATION_MS = 8500;

/** One conversation starter card, separate from the feed heading. */
export function GroceryTopicInspiration({ onUseIdea, refreshKey }: Props) {
  const [prompts, setPrompts] = useState<GroceryConversationPrompt[]>([]);
  const [active, setActive] = useState(0);
  const [interactionPaused, setInteractionPaused] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/community/inspiration', { signal: controller.signal, cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error('inspiration_unavailable');
        return await response.json() as { prompts?: GroceryConversationPrompt[] };
      })
      .then(response => {
        if (controller.signal.aborted || !Array.isArray(response.prompts)) return;
        const safe = response.prompts.filter(prompt =>
          typeof prompt.question === 'string' && prompt.question.length < 220 &&
          typeof prompt.sourceUrl === 'string' && prompt.sourceUrl.startsWith('https://') &&
          typeof prompt.category === 'string');
        setPrompts(orderConversationQuestions(safe));
        setActive(0);
      })
      .catch(() => { if (!controller.signal.aborted) setPrompts([]); });
    return () => controller.abort();
  }, [refreshKey]);

  useEffect(() => {
    if (interactionPaused || prompts.length < 2 ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const rotation = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        setActive(index => (index + 1) % prompts.length);
      }
    }, ROTATION_MS);
    return () => window.clearInterval(rotation);
  }, [interactionPaused, prompts.length]);

  const prompt = prompts[active % prompts.length];
  const hasSourceStory = !!prompt && prompt.sourceName !== 'Youdle Community';
  const storyHref = prompt ? '/community/stories/' + encodeURIComponent(prompt.id) : '/community';

  useEffect(() => {
    if (prompt && !document.hidden) markConversationQuestionSeen(prompt.question);
  }, [prompt?.question]);

  useEffect(() => {
    const resumeOnReturn = () => {
      if (!document.hidden) setInteractionPaused(false);
    };
    window.addEventListener('pageshow', resumeOnReturn);
    document.addEventListener('visibilitychange', resumeOnReturn);
    return () => {
      window.removeEventListener('pageshow', resumeOnReturn);
      document.removeEventListener('visibilitychange', resumeOnReturn);
    };
  }, []);

  const canHover = () =>
    Boolean(window.matchMedia?.('(hover: hover) and (pointer: fine)').matches);

  return (
    <aside className="topic-inspiration" aria-label="Grocery conversation starter"
      onMouseEnter={() => { if (canHover()) setInteractionPaused(true); }}
      onMouseLeave={() => setInteractionPaused(false)}
      onFocusCapture={event => {
        if ((event.target as HTMLElement).matches?.(':focus-visible')) setInteractionPaused(true);
      }}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setInteractionPaused(false);
        }
      }}>
      <div className="topic-inspiration-top">
        <span className="topic-inspiration-label">
          <span className="topic-inspiration-dot" aria-hidden="true" />
          Conversation starter
        </span>
        {prompts.length > 1 && (
          <span className="topic-inspiration-index" aria-label={`Question ${active + 1} of ${prompts.length}`}>
            {active + 1} of {prompts.length}
          </span>
        )}
      </div>
      <p className="topic-inspiration-question" aria-live="off">
        {prompt ? prompt.question : 'What grocery find would you recommend to a friend?'}
      </p>
      <div className="topic-inspiration-bottom">
        <span className="topic-inspiration-source">
          {hasSourceStory
            ? <>Inspired by <Link href={storyHref} title={`Read the story behind this question from ${prompt.sourceName}`}>{prompt.sourceName}</Link></>
            : 'From Youdle'}
        </span>
        <div className="topic-inspiration-actions" aria-label="Conversation starter controls">
          {prompts.length > 1 && (
            <>
              <button className="topic-inspiration-next" type="button"
                aria-label="Previous conversation starter"
                onClick={() => setActive(index => (index - 1 + prompts.length) % prompts.length)}>
                <FiChevronLeft aria-hidden="true" size={15} /> Previous
              </button>
              <button className="topic-inspiration-next" type="button"
                aria-label="Next conversation starter"
                onClick={() => setActive(index => (index + 1) % prompts.length)}>
                Next <FiChevronRight aria-hidden="true" size={15} />
              </button>
            </>
          )}
          <button className="topic-inspiration-use" type="button"
            onClick={() => onUseIdea(
              prompt?.question || 'What grocery find would you recommend to a friend?',
              prompt?.category || 'Reviews & Recommendations',
            )}>
            Start a conversation
          </button>
        </div>
      </div>
    </aside>
  );
}
